// src/utils/firestoreService.js
import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  getDocs, getDoc, query, where, orderBy, serverTimestamp, limit
} from "firebase/firestore";
import {
  ref, uploadBytes, getDownloadURL, deleteObject
} from "firebase/storage";
import { db, storage } from "../firebase";

// ─── PRODUCTS ────────────────────────────────────────────────
export const getProducts = async (category = null) => {
  let q = category
    ? query(collection(db, "products"), where("category", "==", category), where("active", "==", true), orderBy("createdAt", "desc"))
    : query(collection(db, "products"), where("active", "==", true), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getAllProducts = async () => {
  const snap = await getDocs(query(collection(db, "products"), orderBy("createdAt", "desc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getProduct = async (id) => {
  const snap = await getDoc(doc(db, "products", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const getFeaturedProducts = async () => {
  const q = query(collection(db, "products"), where("featured", "==", true), where("active", "==", true), limit(8));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addProduct = async (data, imageFiles) => {
  const imageUrls = await Promise.all(
    imageFiles.map(async (file) => {
      const storageRef = ref(storage, `products/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      return getDownloadURL(storageRef);
    })
  );
  return addDoc(collection(db, "products"), {
    ...data,
    images: imageUrls,
    active: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateProduct = async (id, data, newImageFiles = []) => {
  let newUrls = [];
  if (newImageFiles.length > 0) {
    newUrls = await Promise.all(
      newImageFiles.map(async (file) => {
        const storageRef = ref(storage, `products/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
      })
    );
  }
  const updateData = {
    ...data,
    updatedAt: serverTimestamp()
  };
  if (newUrls.length > 0) {
    updateData.images = [...(data.images || []), ...newUrls];
  }
  return updateDoc(doc(db, "products", id), updateData);
};

export const deleteProduct = async (id) => {
  await deleteDoc(doc(db, "products", id));
};

export const deleteProductImage = async (imageUrl) => {
  try {
    const imageRef = ref(storage, imageUrl);
    await deleteObject(imageRef);
  } catch (e) {
    console.warn("Could not delete image:", e);
  }
};

// ─── CATEGORIES ──────────────────────────────────────────────
export const getCategories = async () => {
  const snap = await getDocs(collection(db, "categories"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addCategory = async (data) => {
  return addDoc(collection(db, "categories"), { ...data, createdAt: serverTimestamp() });
};

export const deleteCategory = async (id) => deleteDoc(doc(db, "categories", id));

// ─── ORDERS ──────────────────────────────────────────────────
export const createOrder = async (orderData) => {
  return addDoc(collection(db, "orders"), {
    ...orderData,
    status: "pending",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const getOrders = async () => {
  const snap = await getDocs(query(collection(db, "orders"), orderBy("createdAt", "desc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getOrder = async (id) => {
  const snap = await getDoc(doc(db, "orders", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const updateOrderStatus = async (id, status) => {
  return updateDoc(doc(db, "orders", id), { status, updatedAt: serverTimestamp() });
};

export const getOrdersByEmail = async (email) => {
  const q = query(collection(db, "orders"), where("customer.email", "==", email), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// ─── REVIEWS ─────────────────────────────────────────────────
export const addReview = async (productId, reviewData) => {
  return addDoc(collection(db, "reviews"), {
    ...reviewData,
    productId,
    createdAt: serverTimestamp()
  });
};

export const getReviews = async (productId) => {
  const q = query(collection(db, "reviews"), where("productId", "==", productId), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};
